import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import Pagination from '@mui/material/Pagination';
import { Link, NavLink } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import { useDispatch } from 'react-redux';
import { deleteBranch as deleteBranchFromStore } from '../../redux/slices/branchSlice';
import BreadcrumbsWrapper from '../../components/breadcrumbs'
import { checkPermission } from '../../utils/checkPermissions';
import PermissionWrapper from '../../components/PermissionWrapper';


// Redux imports (optional, if you want to store in redux)
// import { useDispatch } from 'react-redux';
// import { setBranches } from '../../redux/slices/branchSlice';

export default function Branches() {
  const [branchList, setBranchList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [searchPostcode, setSearchPostcode] = useState('');
  const authCtx = useContext(AuthContext);
  const userPermissions = authCtx.permissions;
  
  // Check permission
  const canManageBranches = checkPermission(userPermissions, 'settings', 'manage_branches');

  // Debounce ref
  const debounceRef = useRef();
  const dispatch = useDispatch();

  // Fetch branches API
 const fetchBranches = async (searchNameVal, searchPostcodeVal, pageVal = page) => {
  setLoading(true);
  try {
   
    const payload = {
      page: pageVal,
      limit: 5,
      name: searchNameVal,
      postcode:searchPostcodeVal
    };

    const response = await postRequest('private/BranchList', payload, authCtx.token);
    if (response?.status === 200 && response?.data) {
      setBranchList(response.data.docs || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalDocs(response.data.totalDocs || 0);
    } else {
      setBranchList([]);
      setTotalPages(1);
      setTotalDocs(0);
    }
  } catch (err) {
    setBranchList([]);
    setTotalPages(1);
    setTotalDocs(0);
  }
  setLoading(false);
};

  // Fetch on mount and when page changes
  useEffect(() => {
    fetchBranches(searchName, searchPostcode, page);
    // eslint-disable-next-line
  }, [page, authCtx.token]);

  // Debounced search on name or postcode change
  useEffect(() => {
    setPage(1); // Reset to first page on search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchBranches(searchName, searchPostcode, 1);
    }, 400);
    // eslint-disable-next-line
  }, [searchName, searchPostcode]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };


const handleDeleteBranch = async (id) => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: 'You will not be able to recover this branch!',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#bb0000',
    cancelButtonColor: '#aaa',
    confirmButtonText: 'Yes, delete it!',
  });

  if (result.isConfirmed) {
    try {
      const response = await postRequest(`private/deleteBranch/${id}`, {}, authCtx.token);
      if (response?.status === 200) {
        dispatch(deleteBranchFromStore(id));
        Swal.fire('Deleted!', 'Branch has been deleted.', 'success');

        const payload = {
          page,
          limit: 5,
          name: searchName,
          postcode:searchPostcode
        };
        const listResponse = await postRequest('private/BranchList', payload, authCtx.token);
        const docs = listResponse?.data?.docs || [];
        const totalPagesAfterDelete = listResponse?.data?.totalPages || 1;

        if (docs.length === 0 && page > 1) {
          // ✅ If no data left on page, go to previous page
          setPage((prev) => prev - 1);
        } else {
          // ✅ Otherwise just update the list
          setBranchList(docs);
          setTotalPages(totalPagesAfterDelete);
        }
      } else {
        Swal.fire('Failed!', response?.message || 'Something went wrong.', 'error');
      }
    } catch (err) {
      Swal.fire('Error!', err?.message || 'Something went wrong.', 'error');
    }
  }
};



  return (
    <PermissionWrapper
      category="settings"
      permission="manage_branches"
      pageTitle="Branches"
      breadcrumbs={[
        { label: 'Dashboard', path: '/dashboard' },
        { label: 'Branches', path: '' },
      ]}
    >
      <>
       <BreadcrumbsWrapper
               items={[
                { label: 'Dashboard', path: '/dashboard' },
                { label: 'Branches', path: '' },
                  ]}
                   />
          <br></br>
      <div className='page-header'>
        <h1 className='mt-0'>Branches</h1>
        {canManageBranches && (
          <div className='header-right-btn'>
            <Stack spacing={2} direction="row">
              <Button variant="outlined" className='red-outline-btn text-capitalize'>
                <NavLink to="/branches/create" className="red-link-text">
                  <PersonAddOutlinedIcon /> &nbsp; Add New Branch
                </NavLink>
              </Button>
            </Stack>
          </div>
        )}
      </div>
      <div className='page-top-formview mb-3'>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="searchUsername">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                value={searchName}
                onChange={e => {
                  setSearchName(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
            <Form.Group as={Col} controlId="searchPostcode">
              <Form.Label>Postcode</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter postcode"
                value={searchPostcode}
                onChange={e => {
                  setSearchPostcode(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
          </Row>
        </Form>
      </div>

      <div>
        <Table striped responsive bordered>
          <thead>
            <tr>
              <th>Name</th>
              <th>Town</th>
              <th>Postcode</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : branchList.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  No branches found.
                </td>
              </tr>
            ) : (
              branchList.map((branch) => (
                <tr key={branch._id}>
                  <th>{branch.name}</th>
                  <td>{branch?.address?.town}</td>
                  <td>{branch?.address?.postcode}</td>
                  <td>
                    <span
                      className='red-link-text cursor-pointer w-18'
                      title="View"
                      style={{ marginRight: 8 }}
                    >
                      <NavLink to={`/branches/view/${branch.slug}/${branch._id}`} className="red-link-text">
                        <VisibilityOutlinedIcon />
                      </NavLink>
                    </span>
                    <span
                      className='red-link-text cursor-pointer w-18'
                      title="Edit"
                      style={{ marginRight: 8 }}
                    >
                      <NavLink to={`/branches/edit/${branch._id}`} className="red-link-text">
                        <EditOutlinedIcon />
                      </NavLink>
                    </span>
                 <span
                className='red-link-text cursor-pointer w-18'
                title="Delete"
                onClick={() => handleDeleteBranch(branch._id)}
              >
                <DeleteOutlineOutlinedIcon />
              </span>

                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {/* Pagination */}
      {branchList.length > 0 && (
  <>
    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8, marginBottom: 8 }}>
      <span className="text-muted small">
        Showing {((page - 1) * 5) + 1}-{Math.min(page * 5, totalDocs)} of {totalDocs} branches
      </span>
    </div>
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        color="primary"
        shape="rounded"
        size="medium"
        sx={{
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                    color: '#fff !important',
                  },
                  '& .MuiPaginationItem-root': {
                    color: 'var(--bs-red-color)',
                    borderColor: 'var(--bs-red-color)',
                  },
                }}
      />
    </div>
  </>
)}

      </div>
      </>
    </PermissionWrapper>
  );
}